"use client";

import { useCallback, useState } from "react";
import type {
  AnnouncementRecipient,
  SendAnnouncementChunkRequest,
  SendAnnouncementChunkResult,
  SendAnnouncementFailure,
  SendAnnouncementStatus,
} from "../types";

export const DEFAULT_CHUNK_SIZE = 25;

export function chunkRecipients<T>(
  recipients: T[],
  chunkSize: number = DEFAULT_CHUNK_SIZE,
): T[][] {
  if (recipients.length === 0) return [];
  const chunks: T[][] = [];
  for (let i = 0; i < recipients.length; i += chunkSize) {
    chunks.push(recipients.slice(i, i + chunkSize));
  }
  return chunks;
}

export interface UseSendAnnouncementOptions {
  chunkSize?: number;
}

export interface SendAnnouncementInput {
  recipients: AnnouncementRecipient[];
  subject: string;
  body: string;
  preheader?: string;
  replyTo?: string;
  fromName?: string;
  signatureName?: string;
  delayMs?: number;
}

export interface UseSendAnnouncementResult {
  status: SendAnnouncementStatus;
  sentCount: number;
  totalCount: number;
  failures: SendAnnouncementFailure[];
  error: string | null;
  send: (input: SendAnnouncementInput) => Promise<void>;
  reset: () => void;
}

type SendChunkFn = (
  payload: SendAnnouncementChunkRequest,
) => Promise<SendAnnouncementChunkResult>;

/**
 * Sends announcement emails in chunks via an injected sendChunk function
 * (typically a POST to /api/admin/send-emails). Tracks progress and failures.
 */
export function useSendAnnouncement(
  sendChunk: SendChunkFn,
  options: UseSendAnnouncementOptions = {},
): UseSendAnnouncementResult {
  const chunkSize = options.chunkSize ?? DEFAULT_CHUNK_SIZE;

  const [status, setStatus] = useState<SendAnnouncementStatus>("idle");
  const [sentCount, setSentCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [failures, setFailures] = useState<SendAnnouncementFailure[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setStatus("idle");
    setSentCount(0);
    setTotalCount(0);
    setFailures([]);
    setError(null);
  }, []);

  const send = useCallback(
    async (input: SendAnnouncementInput) => {
      const {
        recipients,
        subject,
        body,
        preheader,
        replyTo,
        fromName,
        signatureName,
        delayMs,
      } = input;

      setStatus("sending");
      setSentCount(0);
      setTotalCount(recipients.length);
      setFailures([]);
      setError(null);

      const chunks = chunkRecipients(recipients, chunkSize);
      let successTotal = 0;
      const allFailures: SendAnnouncementFailure[] = [];

      try {
        for (const chunk of chunks) {
          const result = await sendChunk({
            recipients: chunk,
            subject,
            body,
            preheader,
            replyTo,
            fromName,
            signatureName,
            delayMs,
          });

          successTotal += result.successCount;
          for (const item of result.results) {
            if (!item.success) {
              allFailures.push({
                email: item.email,
                error: item.error ?? "Unknown error",
              });
            }
          }

          setSentCount(successTotal);
          setFailures([...allFailures]);
        }

        setStatus("done");
      } catch (err) {
        setSentCount(successTotal);
        setFailures(allFailures);
        setError(err instanceof Error ? err.message : "Failed to send emails");
        setStatus("error");
      }
    },
    [chunkSize, sendChunk],
  );

  return {
    status,
    sentCount,
    totalCount,
    failures,
    error,
    send,
    reset,
  };
}
